import {RequestInfo} from "@khan/shared-core";

export const DEFAULT_DONATE_URL =
    "https://donate.khanacademy.org/campaign/580662/donate";

// This is a webflow site, and has nothing to do with Classy
const INDIA_DONATE_URL = "https://india.khanacademy.org/donate";
const INDIA = "IN";

// https://support.classy.org/s/article/a-guide-to-pass-through-parameters#list
// https://support.classy.org/s/article/how-to-use-source-codes
export type ClassyPassThroughParams = {
    // Source code param for kaid
    c_src?: string | null;
    // Source code param for location
    c_src2?: string;
    // Preselected amount
    amount?: string;
    // 0 for 1-time, 1 for monthly, 4 for yearly
    recurring?: string;
    // what to display in the 1st donation option
    preset1?: string;
    // what to display in the 2nd donation option
    preset2?: string;
    // what to display in the 3rd donation option
    preset3?: string;
    // what to display in the 4th donation option
    preset4?: string;
    // The users email, to be pre-populated on the donation form
    email?: string;
};

// Builds the url for that the donation banner links to. Pass-through params
// can be supplied by the caller and are appended to the URL in the correct
// format, based on the type of URL it is.
// We currently support 3 different forms of URLs:
// * classic Classy campaigns, where the URL is built based on the campaign id
// * Classy Studio campaigns, where the URL is provided and uses the standard
//   set of passthrough parameters
// * Classy Studio campaigns - checkout flow, where the URL is provided and
//   uses its own variation of passthrough parameters
export default function getClassyCampaignURL(
    kaLocale: string,
    passThroughParams?: ClassyPassThroughParams,
    classyCampaignId?: string,
    donateURL?: string,
): string {
    if (RequestInfo.remoteCountry === INDIA) {
        return INDIA_DONATE_URL;
    }

    // Classy doesn't have translation capabilities, instead they
    // create a new campaign for each language. If we have a translated
    // campaign, the id is listed here, if not, we'll default sending
    // the user to the "en" page, which has a google translate widget
    // This strategy works largely because we don't get a lot of non
    // "en" donations, if that changes in the future we may want to
    // re-evaluate.
    const localizedCampaignId = {es: "422869", fr: "422875"}[kaLocale] || "";

    // We populate our donate URL in priority order based on our provided
    // properties.
    // If we are in the `es` or `fr` kaLocale, we will use the campaign pages
    // built specifically for those locales.
    // If we are provided a campaign ID but not a donate URL, we will use the
    // campaign ID and send the user to the classic Classy campaign page.
    // If we are provided a donate URL, we will use that URL.
    // If we are not provided a campaign ID nor a donate URL, we will use the
    // default donate URL.
    // TODO (russell): We want to deprecate support for the classic Classy
    // campaign pages and only support the Classy Studio pages and URL pattern.
    let baseUrl = "";
    if (localizedCampaignId) {
        baseUrl = `https://donate.khanacademy.org/give/${localizedCampaignId}/#!/donation/checkout`;
    } else if (classyCampaignId && !donateURL) {
        baseUrl = `https://donate.khanacademy.org/give/${classyCampaignId}/#!/donation/checkout`;
    } else {
        baseUrl = donateURL || DEFAULT_DONATE_URL;
    }

    // To determine if the donate URL is a direct link to the Classy Studio
    // checkout flow or if it's a link to the campaign landing page, we need to
    // parse the URL for the "https://donate.khanacademy.org/checkout", where
    // "https://donate.khanacademy.org/checkout" URLs link directly to the
    // Classy Studio checkout flow and require a slightly different variation
    // of our passthrough params.
    const isCheckoutPage = baseUrl.includes(
        "https://donate.khanacademy.org/checkout",
    );

    let paramStr = buildPassThroughParams(passThroughParams, isCheckoutPage);

    // Checkout page URLs will come with a couple of query parameters already
    // in them. We need to move those query params to the end of the string
    // because we need the c_src and c_src2 params returning from
    // buildPassThroughParams to be the first query params to ensure that
    // Classy tracks them correctly.
    if (isCheckoutPage) {
        const urlParts = baseUrl.split("?");
        baseUrl = urlParts[0];
        paramStr += "&" + urlParts[1];
    }

    return `${baseUrl}${paramStr}`;
}

// Properly builds a query string based on params passed in.
// Unfortunately, Classy does not support custom pass-through parameters.
// For KAID and banner id, we hijack two source-code parameters which usually
// used for analytics they look like this: c_src=<kaid> c_src2=Webapp.<banner-id>.
// They must be passed in before the other params.
function buildPassThroughParams(
    params: ClassyPassThroughParams | undefined,
    isCheckoutPage: boolean,
): string {
    if (!params) {
        return "";
    }

    let cleanParams = new URLSearchParams(
        Object.fromEntries(
            Object.entries(params)
                .filter(([_, v]) => v != null)
                .sort((a, b) => {
                    // c_src and c_src2 must come first
                    if (a[0] === "c_src") {
                        return -1;
                    }
                    if (a[0] === "c_src2" && b[0] !== "c_src") {
                        return -1;
                    }

                    return 0;
                }) as Array<[string, string]>,
        ),
    ).toString();

    if (cleanParams !== "") {
        cleanParams = `?${cleanParams}`;
    }

    // Extra parameters and parameter substitutions are needed to make the
    // checkout page work properly. Where the campaign landing page uses a
    // "recurring" param of 0, 1, and 4 to represent one time, monthly, and
    // yearly donations, the checkout page uses a "frequency" param with values
    // of "monthly" and "yearly" to represent those same recurring donations. A
    // one-time donation doesn't need any special parameter. Additionally, the
    // checkout page needs to set the currency to determine payment options and
    // we always run our campaigns in USD. Finally, the step parameter is
    // needed to indicate which part of the checkout flow the user should be
    // taken to, and we are always taking the user to step 0.
    if (isCheckoutPage) {
        cleanParams = cleanParams.replace("&recurring=0", "");
        cleanParams = cleanParams.replace("recurring=1", "frequency=monthly");
        cleanParams = cleanParams.replace("recurring=4", "frequency=yearly");
        cleanParams += "&currency=USD&step=0";
    }

    return cleanParams;
}
